import {
  addDays, addMonths,
  addYears,
  compareAsc,
  differenceInCalendarMonths,
  differenceInMonths, endOfMonth,
  startOfToday,
  startOfYear
} from "date-fns";
import {
  getDateToday,
  getISODateFromDate, isoToLocalDate,
  numberToMoneyFormat,
  numberToPercentFormat, roundMoney
} from "../../stores/StoreUtilities";
import Person from "../../model/Person";
import {ISocialSecurityBenefit} from "../../model/changes/SocialSecurityStrategyChange";
import cloneDeep from "lodash.clonedeep";
import {globalColors} from "../../styles/globalStyles";


export type RetirementBenefits = {
  myStart?: Date,
  myBenefit: number,
  myPercent: number,
  myDescription: string
  spousalStart?: Date
  spousalBenefit: number
  spousalPercent: number
  spousalDescription: string
}

class SocialSecurityCalc {
  static getFullRetirementAge(birthDate : Date) : number {
    let age = 67
    let birthYear = birthDate.getFullYear()

    if (birthDate.getMonth() === 0 && birthDate.getDate() === 1) {
      // Handle Jan 1st birth date
      --birthYear
    }

    if (birthYear >= 1960) {
      age = 67
    } else if (birthYear === 1959) {
      age = 66 + 10/12
    } else if (birthYear === 1958) {
      age = 66 + 8/12
    } else if (birthYear === 1957) {
      age = 66 + 6/12
    } else if (birthYear === 1956) {
      age = 66 + 4/12
    } else if (birthYear === 1955) {
      age = 66 + 2/12
    } else if (birthYear >= 1943 && birthYear <= 1954) {
      age = 66
    } else if (birthYear === 1942) {
      age = 65 + 10/12
    } else if (birthYear === 1941) {
      age = 65 + 8/12
    } else if (birthYear === 1940) {
      age = 65 + 6/12
    } else if (birthYear === 1939) {
      age = 65 + 4/12
    } else if (birthYear === 1938) {
      age = 65 + 2/12
    } else {
      age = 65
    }

    return age
  }

  static getDelayedRetirementAnnualPercent(birthDate: Date) {
    let percent = 0
    let birthYear = birthDate.getFullYear()

    if (birthDate.getMonth() === 0 && birthDate.getDate() === 1) {
      // Handle Jan 1st birth date
      --birthYear
    }

    if (birthYear >= 1943) {
      percent = 0.08
    } else if (birthYear >= 1941) {
      percent = 0.075
    } else if (birthYear >= 1939) {
      percent = 0.07
    } else if (birthYear >= 1937) {
      percent = 0.065
    } else if (birthYear >= 1935) {
      percent = 0.06
    } else if (birthYear >= 1933) {
      percent = 0.055
    } else if (birthYear >= 1931) {
      percent = 0.05
    } else if (birthYear >= 1929) {
      percent = 0.045
    } else if (birthYear >= 1927) {
      percent = 0.04
    } else if (birthYear >= 1925) {
      percent = 0.035
    } else {
      percent = 0.03
    }

    return percent
  }

  /* Rules for social security benefits
    Personal Benefit
    Age >= 62 && Age < FRA:  FRB - (1% * 5/9 for first 36 mon) - (1% * 5/12 for remaining months)
    FRA: FRB & 100%
    Age > FRA:  FRB + (Delayed % * numMonths)
   */

  static calculateRetirementBenefits(birthDate: Date, startDate: Date, fullBenefit: number,
                                     spouseBirthDate?: Date, spouseStartDate?: Date, spouseFullBenefit?: number) : RetirementBenefits {
    const benefits: RetirementBenefits = {
      myStart: undefined,
      myBenefit: 0,
      myPercent: 0,
      myDescription: "",
      spousalStart: undefined,
      spousalBenefit: 0,
      spousalPercent: 0,
      spousalDescription: ""
    }

    let fullRetirementAgeInMonths = Math.round(this.getFullRetirementAge(birthDate) * 12)
    const birthDay = birthDate.getDate()
    if (birthDay === 1) {
      --fullRetirementAgeInMonths
    }
    let retirementAgeInMonths = Math.max(Math.min(differenceInMonths(startDate, birthDate), 70 * 12), 62 * 12)

    if (!fullBenefit) {
      benefits.myStart = startDate
      benefits.myDescription = "No Retirement Benefit"
    } else if (retirementAgeInMonths < fullRetirementAgeInMonths) {
      // Calculate reduced benefit
      let percentReduction = 0
      let numMonths = fullRetirementAgeInMonths - retirementAgeInMonths
      if (numMonths <= 36) {
        percentReduction = numMonths * (0.01 * 5/9)
      } else {
        percentReduction = (36 * (0.01 * 5/9)) + ((numMonths - 36) * (0.01 * 5/12))
      }
      benefits.myStart = startDate
      benefits.myBenefit = Math.round(fullBenefit + (fullBenefit * -percentReduction))
      benefits.myPercent = 1 - percentReduction
      benefits.myDescription = `${numberToPercentFormat(benefits.myPercent, 0)} Retirememt Benefit`
    } else if (retirementAgeInMonths > fullRetirementAgeInMonths) {
      // Calculate increased benefit
      const delayedPercent = this.getDelayedRetirementAnnualPercent(birthDate)
      const numMonths = retirementAgeInMonths - fullRetirementAgeInMonths
      const percentIncrease = numMonths * (delayedPercent / 12)
      benefits.myBenefit = Math.round(fullBenefit + (fullBenefit * (percentIncrease)))
      benefits.myStart = startDate
      benefits.myPercent = 1 + percentIncrease
      benefits.myDescription = `${numberToPercentFormat(benefits.myPercent, 0)} Retirememt Benefit`
    } else { // Full benefit
      benefits.myBenefit = fullBenefit
      benefits.myStart = startDate
      benefits.myPercent = 1
      benefits.myDescription = `${numberToPercentFormat(benefits.myPercent, 0)} Retirememt Benefit`
    }

    if (spouseBirthDate && spouseFullBenefit && spouseStartDate) {
      benefits.spousalBenefit = Math.round(spouseFullBenefit / 2) - fullBenefit
      benefits.spousalPercent = 1
      benefits.spousalDescription = "100% Spousal Benefit"

      // fullRetirementAgeInMonths = Math.round(this.getFullRetirementAge(spouseBirthDate) * 12)
      // retirementAgeInMonths = Math.max(Math.min(differenceInMonths(spouseStartDate, birthDate), 70 * 12), 62 * 12)

      let spousalBenefitStart = startDate
      // Spousal benefits start at the latter of their or their spouses start date
      if (compareAsc(startDate, spouseStartDate) < 0) {
        spousalBenefitStart = spouseStartDate
        retirementAgeInMonths = Math.max(Math.min(differenceInMonths(spousalBenefitStart, birthDate), 70 * 12), 62 * 12)
      }

      // if worker has retired on or after spouse, then worker is entitled to spousal benefit if greater
      if (retirementAgeInMonths < fullRetirementAgeInMonths) {
        // Calculate reduced benefit
        let percentReduction = 0
        let numMonths = fullRetirementAgeInMonths - retirementAgeInMonths
        if (numMonths <= 36) {
          percentReduction = numMonths * (0.01 * 25/36)
        } else {
          percentReduction = (36 * (0.01 * 25/36)) + ((numMonths - 36) * (0.01 * 5/12))
        }
        benefits.spousalBenefit = Math.round(benefits.spousalBenefit + (benefits.spousalBenefit * -percentReduction))
        benefits.spousalPercent = 1 - percentReduction
        benefits.spousalDescription = `${numberToPercentFormat(benefits.spousalPercent , 0)} Spousal Benefit`
      }

      if (benefits.spousalBenefit < 0) {
        benefits.spousalBenefit = 0
        benefits.spousalStart = undefined
        benefits.spousalDescription = ""
      }

      // If worker retires on or after spouse, they may take the spousal benefit, if greater
      if (compareAsc(startDate, spouseStartDate) >= 0) {
        benefits.spousalBenefit = Math.min(benefits.spousalBenefit + benefits.myBenefit, spouseFullBenefit / 2)
        if (benefits.spousalBenefit > benefits.myBenefit) {
          benefits.spousalStart = spouseStartDate
          benefits.myStart = undefined
          benefits.spousalStart = startDate
        }
      } else { // Worker must take their own benefit, if any, until their spouse starts receiving benefits
        benefits.spousalBenefit = Math.min(benefits.spousalBenefit + benefits.myBenefit, spouseFullBenefit / 2)
        if (benefits.spousalBenefit > benefits.myBenefit) {
          benefits.spousalStart = spouseStartDate
          benefits.spousalDescription = `${numberToPercentFormat(benefits.spousalPercent, 0)} Spousal Benefit`
        } else {
          benefits.spousalStart = undefined
        }
      }
    }

    return benefits
  }

  static maximizeRetirementBenefits(persons: Person[], fullBenefit: number, infRate: number, spouseFullBenefit?: number) : ISocialSecurityBenefit[] {
    const startTime = new Date()

    let benefits: ISocialSecurityBenefit[] = []
    let maxLifetimeBenefits = 0

    const birthDate = isoToLocalDate(persons[0].birthDate)
    const birthDate2 = persons.length > 1 ? isoToLocalDate(persons[1].birthDate) : undefined
    let count = 0

    for (let year = 62; year < 70; year++) {
      let startDate = addYears(birthDate, year)
      for (let month = 0; month < 12; month++) {
        if (birthDate2) {
          for (let year2 = 62; year2 < 70; year2++) {
            let startDate2 = addYears(birthDate2, year2)
            for (let month2 = 0; month2 < 12; month2++) {
              const benefit1 = this.calculateRetirementBenefits(birthDate, startDate, fullBenefit, birthDate2, startDate2, spouseFullBenefit)
              const benefit2 = this.calculateRetirementBenefits(birthDate2, startDate2, spouseFullBenefit ?? 0, birthDate, startDate, fullBenefit)
              const ssBenefits: ISocialSecurityBenefit[] = [
                {
                  personId: persons[0].id,
                  fullBenefit: fullBenefit,
                  personalStart: benefit1.myStart ? getISODateFromDate(benefit1.myStart) : undefined,
                  personalBenefit: benefit1.myStart ? benefit1.myBenefit : 0,
                  personalPercent: benefit1.myStart ? benefit1.myPercent : 0,
                  spousalStart: benefit1.spousalStart ? getISODateFromDate(benefit1.spousalStart) : undefined,
                  spousalBenefit: benefit1.spousalStart ? benefit1.spousalBenefit : 0,
                  spousalPercent: benefit1.spousalStart ? benefit1.spousalPercent : 0,
                  annualInf: infRate
                },
                {
                  personId: persons[1].id,
                  fullBenefit: spouseFullBenefit ?? 0,
                  personalStart: benefit2.myStart ? getISODateFromDate(benefit2.myStart) : undefined,
                  personalBenefit: benefit2.myStart ? benefit2.myBenefit : 0,
                  personalPercent: benefit2.myStart ? benefit2.myPercent : 0,
                  spousalStart: benefit2.spousalStart ? getISODateFromDate(benefit2.spousalStart) : undefined,
                  spousalBenefit: benefit2.spousalStart ? benefit2.spousalBenefit : 0,
                  spousalPercent: benefit2.spousalStart ? benefit2.spousalPercent : 0,
                  annualInf: infRate
                }
              ]
              const lifetimeBenefits = this.calculateLifetimeBenefits(ssBenefits, persons)
              console.log(`${year}-${month+1}/${year2}-${month2+1}: ${numberToMoneyFormat(lifetimeBenefits, 0)}`)
              if (lifetimeBenefits > maxLifetimeBenefits || benefits.length === 0) {
                maxLifetimeBenefits = lifetimeBenefits
                benefits = ssBenefits
              }
              startDate2 = addMonths(startDate2, 1)
              ++count
            }
          }
        } else {
          const benefit = this.calculateRetirementBenefits(birthDate, startDate, fullBenefit)
          const ssBenefit: ISocialSecurityBenefit = {
            personId: persons[0].id,
            fullBenefit: fullBenefit,
            personalStart: getISODateFromDate(startDate),
            personalBenefit: benefit.myBenefit,
            personalPercent: benefit.myPercent,
            spousalBenefit: 0,
            annualInf: infRate
          }
          const lifetimeBenefits = this.calculateLifetimeBenefits([ssBenefit], persons)
          // console.log(`${year}-${month+1}: ${numberToMoneyFormat(lifetimeBenefits, 0)}`)
          if (lifetimeBenefits > maxLifetimeBenefits || benefits.length === 0) {
            maxLifetimeBenefits = lifetimeBenefits
            benefits = [ssBenefit]
          }
        }
        startDate = addMonths(startDate, 1)
        ++count
      }
    }

    const endTime = new Date()
    console.log(`Maximize Count: ${count}`)
    console.log(`Maximize Benefits: ${numberToMoneyFormat(maxLifetimeBenefits)}`)
    console.log(`Maximize Time: ${(endTime.getTime() - startTime.getTime())/1000} sec`)

    return benefits
  }

  static calculateLifetimeBenefits(benefits: ISocialSecurityBenefit[], persons: Person[]): number {

    if (persons.length > 0 && benefits.length > 0) {
      const dates = []
      const person1 = persons[0]
      const benefits1 = benefits[0]
      if (benefits1.personalStart) {
        dates.push(benefits1.personalStart)
      }
      if (benefits1.spousalStart) {
        dates.push(benefits1.spousalStart)
      }
      let person2 = persons.length > 1 ? persons[1] : undefined
      let benefits2 = benefits.length > 1 ? benefits[1] : undefined
      if (benefits2) {
        if (benefits2.personalStart) {
          dates.push(benefits2.personalStart)
        }
        if (benefits2.spousalStart) {
          dates.push(benefits2.spousalStart)
        }
      }

      const minIsoDate = dates.reduce((accum: string, date: string) => accum.localeCompare(date) <= 0 ? accum : date, "2999-12-31")
      const today = startOfToday()
      let minDate = isoToLocalDate(minIsoDate)
      if (minDate.getFullYear() < today.getFullYear()) {
        // Don't start in the past
        minDate = startOfYear(today)
      }
      minDate = addYears(minDate, -1)
      let totalBenefit1 = 0
      let totalBenefit2 = 0

      const dataSeries: { name: string, color: string, data: any[] }[] = []
      let data: [number, number, number?][] = []

      if (benefits1.personalStart) {
        data.push(this.getDataValue(benefits1.personalStart, 0, benefits1.annualInf))
        if (benefits1.spousalStart) {
          const personalData = this.getDateRangeValues(benefits1.personalStart, this.priorMonth(benefits1.spousalStart), benefits1.personalBenefit, benefits1.annualInf)
          let spousalData: [number, number, number][] = []
          let survivorData: [number, number, number][] = []
          if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
            // Take the spouses greater benefit if live longer
            spousalData = this.getDateRangeValues(benefits1.spousalStart, person2.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
            survivorData = this.getDateRangeValues(this.nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
          } else {
            spousalData = this.getDateRangeValues(benefits1.spousalStart, person1.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
          }
          data = [...data, ...personalData, ...spousalData, ...survivorData]
        } else {
          if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
            // Take the spouses greater benefit if live longer
            const personalData = this.getDateRangeValues(benefits1.personalStart, person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
            const spouseData = this.getDateRangeValues(this.nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
            data = [...data, ...personalData, ...spouseData]
          } else {
            const personalData = this.getDateRangeValues(benefits1.personalStart, person1.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
            data = [...data, ...personalData]
          }
        }
      } else if (benefits1.spousalStart) {
        data.push(this.getDataValue(benefits1.spousalStart, 0))
        let spousalData: [number, number, number][] = []
        let survivorData: [number, number, number][] = []
        if (person2 && person1.lifeExpectancyDate > person2.lifeExpectancyDate && benefits2 && benefits2.personalBenefit > benefits1.personalBenefit) {
          // Take the spouses greater benefit if live longer
          spousalData = this.getDateRangeValues(benefits1.spousalStart, person2.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
          survivorData = this.getDateRangeValues(this.nextDay(person2.lifeExpectancyDate), person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
        } else {
          spousalData = this.getDateRangeValues(benefits1.spousalStart, person1.lifeExpectancyDate, benefits1.spousalBenefit, benefits1.annualInf)
        }
        data = [...data, ...spousalData, ...survivorData]
      }
      data.push(this.getDataValue(person1.lifeExpectancyDate, 0))
      dataSeries.push({name: `${person1.nickname}'s Social Security Benefit`, color: globalColors.primary, data: data})
      totalBenefit1 = data.reduce((accum: number, datum: [number, number, number?]) => datum[2] ? accum + datum[2] : accum, 0)
      // console.debug(`${person1.nickname}'s Total Benefit: ${numberToMoneyFormat(totalBenefit1, 0)}`)

      if (benefits2 && person2) {
        data = []
        if (benefits2.personalStart) {
          data.push(this.getDataValue(benefits2.personalStart, 0))
          if (benefits2.spousalStart) {
            const personalData = this.getDateRangeValues(benefits2.personalStart, this.priorMonth(benefits2.spousalStart), benefits2.personalBenefit, benefits2.annualInf)
            let spousalData: [number, number, number][] = []
            let survivorData: [number, number, number][] = []
            if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
              // Take the spouses greater benefit if live longer
              spousalData = this.getDateRangeValues(benefits2.spousalStart, person1.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
              survivorData = this.getDateRangeValues(this.nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
            } else {
              spousalData = this.getDateRangeValues(benefits2.spousalStart, person2.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
            }
            data = [...data, ...personalData, ...spousalData, ...survivorData]
          } else {
            if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
              // Take the spouses greater benefit if live longer
              const personalData = this.getDateRangeValues(benefits2.personalStart, person1.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
              const spouseData = this.getDateRangeValues(this.nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
              data = [...data, ...personalData, ...spouseData]
            } else {
              const personalData = this.getDateRangeValues(benefits2.personalStart, person2.lifeExpectancyDate, benefits2.personalBenefit, benefits2.annualInf)
              data = [...data, ...personalData]
            }
          }
        } else if (benefits2.spousalStart) {
          data.push(this.getDataValue(benefits2.spousalStart, 0))
          let spousalData: [number, number, number][] = []
          let survivorData: [number, number, number][] = []
          if (person2.lifeExpectancyDate > person1.lifeExpectancyDate && benefits1.personalBenefit > benefits2.personalBenefit) {
            // Take the spouses greater benefit if live longer
            spousalData = this.getDateRangeValues(benefits2.spousalStart, person1.lifeExpectancyDate, benefits2.spousalBenefit)
            survivorData = this.getDateRangeValues(this.nextDay(person1.lifeExpectancyDate), person2.lifeExpectancyDate, benefits1.personalBenefit, benefits1.annualInf)
          } else {
            spousalData = this.getDateRangeValues(benefits2.spousalStart, person2.lifeExpectancyDate, benefits2.spousalBenefit, benefits2.annualInf)
          }
          data = [...data, ...spousalData, ...survivorData]
        }
        data.push(this.getDataValue(person2.lifeExpectancyDate, 0))
        dataSeries.push({
          name: `${person2.nickname}'s Social Security Benefit`,
          color: globalColors.secondary,
          data: data
        })
        totalBenefit2 = data.reduce((accum: number, datum: [number, number, number?]) => datum[2] ? accum + datum[2] : accum, 0)
        // console.debug(`${person2.nickname}'s Total Benefit: ${numberToMoneyFormat(totalBenefit2, 0)}`)
      }

      return (totalBenefit1 + totalBenefit2)
    } else {
      return 0
    }
  }

  static nextDay(isoDate: string) {
    const date = addDays(isoToLocalDate(isoDate), 1)
    return getISODateFromDate(date)
  }

  static priorMonth(isoDate: string) {
    const date = endOfMonth(addMonths(isoToLocalDate(isoDate), -1))
    return getISODateFromDate(date)
  }

  static getDataValue(isoDate: string, presentValue: number, infRate: number = 0) : [number, number, number] {
    let futureValue = 0
    let date = isoToLocalDate(isoDate)
    const today = getDateToday()
    if (date.getFullYear() < today.getFullYear()) {
      // Move start up to start of current year if it is in the past
      date = startOfYear(today)
    }

    if (presentValue > 0) {
      const months = differenceInMonths(date, today)
      const rate = infRate / 12
      futureValue = this.futureValue(presentValue, rate, months)
    }
    return [date.getTime(), futureValue, 0]
  }

  static getDateRangeValues(start: string, end: string, presentValue: number, infRate: number = 0) : [number, number, number][] {
      // console.debug(`${start} - ${end}: ${numberToMoneyFormat(presentValue, 0)}`)
      const values: [number, number, number][] = []
      const today = getDateToday()
      let date = isoToLocalDate(start)
      if (date.getFullYear() < today.getFullYear()) {
        // Move start up to start of current year if it is in the past
        date = startOfYear(today)
      }
      const endDate = isoToLocalDate(end)
      let rate = 0

      // Get value at start of range
      let thisYear = today.getFullYear()
      let startYear = date.getFullYear()
      let value = presentValue
      // let years = differenceInCalendarYears(date, today)
      let year: number
      for (year = thisYear + 1; year <= startYear; year++) {
        value += value * infRate
      }
      values.push([date.getTime(), value, 0])

      // Compute yearly intermediate points
      while (date.getFullYear() < endDate.getFullYear()) {
        let nextDate = startOfYear(addYears(date, 1))
        let months = differenceInCalendarMonths(nextDate, date)
        let benefit = Math.round(months * value)
        // console.debug(`${date.getFullYear()}: ${numberToMoneyFormat(value, 0)} x ${months}mos = ${numberToMoneyFormat(benefit, 0)}`)
        values.push([nextDate.getTime(), value, benefit])
        // Get start of next year and new value
        // nextDate = startOfDay(addDays(nextDate, 1))
        ++year
        value += value * infRate
        values.push([nextDate.getTime(), value, 0])
        date = nextDate
      }

      // Handle remaining months
      const months = differenceInCalendarMonths(endDate, date) + 1
      if (months > 0) {
        let benefit = months * value
        // console.debug(`${endDate.getFullYear()}: ${numberToMoneyFormat(value, 0)} x ${months}mos = ${numberToMoneyFormat(benefit, 0)}`)
        values.push([endDate.getTime(), value, benefit])
      }

      return values
    }

  static futureValue(presentValue: number, infRate: number, periods: number): number {
    if (infRate === 0) {
      return presentValue
    } else {
      const futureValue = presentValue * Math.pow(1.0 + infRate, periods)
      return roundMoney(futureValue)
    }
  }
}

export default SocialSecurityCalc